/**
 * @fileoverview Add-activity mutation hook.
 * @description Wraps POST /sales/tenants/:id/activities in a TanStack mutation, invalidating that
 * tenant's activity list and its own "View Tenant" detail (for `activityCount`) on success.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { TENANT_ACTIVITIES_QUERY_KEY } from '@/features/sales/hooks/useTenantActivities'
import { TENANTS_QUERY_KEY } from '@/features/sales/hooks/useTenants'
import { addTenantActivity } from '@/features/sales/services/tenant-activities.service'

import type { AddActivityRequest } from '@/features/sales/services/tenant-activities.service'

/**
 * @description Records a new activity against a tenant and refreshes its activity history.
 * @param {string} tenantId - The tenant the activity is recorded against.
 * @returns {ReturnType<typeof useMutation<Awaited<ReturnType<typeof addTenantActivity>>, unknown,
 * AddActivityRequest>>} The mutation object (mutate with the activity's details, isPending, ...).
 */
export function useAddTenantActivity(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: AddActivityRequest) => addTenantActivity(tenantId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TENANT_ACTIVITIES_QUERY_KEY(tenantId) })
      void queryClient.invalidateQueries({ queryKey: [...TENANTS_QUERY_KEY, 'detail', tenantId] })
    },
  })
}
